import {ChangeEvent, FC, useCallback, useEffect} from 'react';
import MaterialTextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import {InputLabelProps} from '@mui/material';
import {useField} from 'formik';
import {ReactComponent as ErrorIcon} from '../../../../assets/images/error.svg';

const DEFAULT_ROW_SIZE = 3;

interface TextFieldProps {
  name: string,
  id?: string;
  disabled?: boolean;
  required?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  label?: JSX.Element | string;
  autoComplete?: string;
  placeholder?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  apiValidationError?: Nullable<Dictionary<string[]>>;
  showErrorMessage?: boolean;
  multiline?: boolean;
  customInput?: any;
  rows?: number;
  size?: 'small' | 'medium';
  onKeyDown?: any;
  inputLabelProps?: InputLabelProps;
}

const TextField: FC<TextFieldProps> = ({
  label,
  name,
  id,
  disabled = false,
  onChange,
  onBlur,
  onFocus,
  apiValidationError,
  required,
  autoComplete,
  placeholder,
  defaultValue,
  customInput,
  inputLabelProps,
  multiline,
  rows,
  size = 'medium',
  onKeyDown,
  className
}) => {
  const [
    {value},
    {error, touched},
    {setTouched, setValue}
  ] = useField(name);

  const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
          return onChange(event.target.value);
        }

        setValue(event.target.value);
        setTouched(true);
      }, []);

  useEffect(() => {
    if (!value) {
      setValue(defaultValue || '');
    }
  }, []);

  const hasErrors = !!apiValidationError || !!(touched && error);
  const errorMessage = apiValidationError?.[name] || (touched && error);
  const inputProps = (customInput || hasErrors) ? {
    ...(customInput && {
      onChange: handleChange,
      onBlur,
      onFocus,
      value,
      ...customInput
    }),
    ...(hasErrors && {
      endAdornment: (
        <InputAdornment position="end">
          <ErrorIcon />
        </InputAdornment>
      )
    })
  } : null;

  return (
    <MaterialTextField
      variant="outlined"
      required={required}
      label={label}
      name={name}
      id={id}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      autoComplete={autoComplete}
      placeholder={placeholder}
      multiline={multiline}
      rows={multiline && rows ? rows : DEFAULT_ROW_SIZE}
      error={hasErrors}
      helperText={errorMessage}
      InputProps={inputProps}
      InputLabelProps={inputLabelProps}
      inputProps={{'data-testid': id}}
      size={size}
      onKeyDown={onKeyDown}
      className={className}
    />
  );
};

export default TextField;
